"use client";

import { supabase } from "@/lib/supabase";

/** Refresh access tokens this many seconds before PostgREST rejects them. */
const EXPIRY_SKEW_SECONDS = 30;

/**
 * Returns a Supabase access token suitable for `Authorization: Bearer` on
 * Next.js API routes backed by `createApiSupabaseClient`.
 *
 * Callers should use this instead of `session.access_token` from `getSession()`
 * alone — the cached access token can be expired while the refresh token is
 * still valid.
 */
export async function getValidSupabaseAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) return null;

  const nowSec = Math.floor(Date.now() / 1000);
  const expiresAt = session.expires_at ?? 0;
  if (expiresAt > nowSec + EXPIRY_SKEW_SECONDS) {
    return session.access_token;
  }

  const {
    data: { session: refreshed },
    error,
  } = await supabase.auth.refreshSession();
  if (error || !refreshed?.access_token) {
    return null;
  }
  return refreshed.access_token;
}
